// hotel/F&B/CashierDailyReport.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tabs,
  Tab,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Receipt,
  Print,
  PictureAsPdf,
  Description,
  Refresh,
  Search,
  Clear,
  AttachMoney,
  CreditCard,
  Payments,
  School,
  EventNote,
  Today,
  TrendingUp,
  TrendingDown,
  People,
  Restaurant,
  RoomService,
  LocalBar,
  TakeoutDining,
  DeliveryDining,
  Schedule,
  ExpandMore,
  Lock,
  LockOpen,
  CheckCircle,
  Warning,
  History,
  AccessTime,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';

import axios from 'axios';
import { format, subDays } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Sidebar from '../../../../components/sidebars/sidebar_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// Order Types Mapping
const ORDER_TYPES = {
  dine_in: { label: 'Dine In', icon: <Restaurant />, color: '#1976D2' },
  Lounge: { label: 'Lounge/Arena', icon: <LocalBar />, color: '#9C27B0' },
  room_service: { label: 'Room Service', icon: <RoomService />, color: '#2E7D32' },
  takeaway: { label: 'Takeaway', icon: <TakeoutDining />, color: '#ED6C02' },
  delivery: { label: 'Delivery', icon: <DeliveryDining />, color: '#0288D1' }
};

// Payment Methods
const PAYMENT_METHODS = {
  Cash: { label: 'Cash', icon: <AttachMoney />, color: '#4CAF50' },
  'POS Terminal': { label: 'POS', icon: <CreditCard />, color: '#2196F3' },
  'Bank Transfer': { label: 'Transfer', icon: <Payments />, color: '#9C27B0' },
  Complimentary: { label: 'Complimentary', icon: <School />, color: '#9E9E9E' }
};

const StatsCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6]
  }
}));

const ReportHeader = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
  color: 'white',
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden'
}));

// Helper Functions
const getCurrentStaffId = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.staff_id || storedProfile.id || storedProfile.staffId || null;
  } catch (error) {
    console.error('Error getting staff ID:', error);
    return null;
  }
};

const getCurrentStaffName = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return `${storedProfile.first_name || ''} ${storedProfile.other_names || ''}`.trim() || storedProfile.name || 'Staff';
  } catch (error) {
    console.error('Error getting staff name:', error);
    return 'Staff';
  }
};

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'N/A';
  try {
    const date = new Date(dateTimeStr);
    return format(date, 'dd/MM/yyyy HH:mm:ss');
  } catch {
    return dateTimeStr;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
};

const formatTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'N/A';
  try {
    const date = new Date(dateTimeStr);
    return format(date, 'HH:mm:ss');
  } catch {
    return dateTimeStr;
  }
};

/**
 * API Service - Updated to support shift filtering and report type
 */
const apiService = {
  getCashierDailyReport: async (hotelCode, date, format = 'json', shiftId = null, reportType = 'shift') => {
    const params = {
      hotel_code: hotelCode,
      date: date,
      format: format,
      report_type: reportType
    };
    
    if (shiftId && shiftId !== 'all' && shiftId !== '') {
      params.shift_id = shiftId;
    }
    
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/cashier_daily_report.php`, { params });
    return response.data;
  }
};

/**
 * Main CashierDailyReport Component
 */
const CashierDailyReport = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Report Type State - 'shift' (8am-7:30am) or 'calendar' (midnight-midnight)
  const [reportType, setReportType] = useState('shift');
  
  // Shift Filter State
  const [selectedShift, setSelectedShift] = useState('');
  const [shifts, setShifts] = useState([]);
  
  // Data States
  const [reportData, setReportData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [overallStats, setOverallStats] = useState(null);
  const [waiterStats, setWaiterStats] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [orderTypeStats, setOrderTypeStats] = useState({});
  const [hourlyBreakdown, setHourlyBreakdown] = useState([]);
  const [dayClosure, setDayClosure] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [waiters, setWaiters] = useState([]);
  const [selectedShiftDetails, setSelectedShiftDetails] = useState(null);

  // Filter States
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchQuery, setSearchQuery] = useState('');
  const [filterWaiter, setFilterWaiter] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');

  // Dialog States
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data when date, shift, or report type changes
  useEffect(() => {
    loadReport();
  }, [selectedDate, selectedShift, reportType]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await apiService.getCashierDailyReport(
        getHotelCode(), 
        selectedDate, 
        'json', 
        selectedShift, 
        reportType
      );
      
      if (data.success) {
        setReportData(data);
        setTransactions(data.transactions || []);
        setOverallStats(data.overall_statistics);
        setWaiterStats(data.waiter_statistics || []);
        setPaymentStats(data.payment_method_statistics);
        setOrderTypeStats(data.order_type_statistics || {});
        setHourlyBreakdown(data.hourly_breakdown || []);
        setDayClosure(data.day_closure);
        setHotel(data.hotel);
        setWaiters(data.waiters || []);
        setShifts(data.shifts || []);
        setSelectedShiftDetails(data.selected_shift || null);
        
        console.log('Report loaded - Report Type:', reportType, 'Shift:', data.shift_id, 'Transactions:', data.transactions?.length);
      } else {
        showSnackbar(data.error || 'Failed to load report', 'error');
      }
    } catch (error) {
      console.error('Error loading report:', error);
      showSnackbar('Failed to load report: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadReport();
      showSnackbar('Report refreshed successfully', 'success');
    } finally {
      setRefreshing(false);
    }
  };

  // Handle report type change
  const handleReportTypeChange = (event, newReportType) => {
    if (newReportType !== null) {
      setReportType(newReportType);
      setSelectedShift(''); // Reset shift filter when changing report type
      showSnackbar(`Switched to ${newReportType === 'shift' ? 'Shift Day' : 'Calendar Day'} report`, 'info');
    }
  };

  /**
   * Enhanced Thermal Printer Print with high contrast and bold text
   */
/**
 * Enhanced Thermal Printer Print with MAXIMUM CONTRAST for thermal printers
 * Uses pure black/white only, no grays, bold fonts throughout
 */
const printThermalReport = async () => {
  try {
    const hotelName = hotel?.hotel_name || 'Frankville Hotels';
    const hotelAddress = hotel?.hotel_address || '';
    const hotelPhone = hotel?.hotel_phone || '';
    const reportDate = formatDate(selectedDate);
    const stats = overallStats || {};
    const paymentStatsData = paymentStats || {};
    
    // Calculate early morning stats
    const earlyMorningCount = transactions.filter(t => {
      const paymentHour = new Date(t.payment_date).getHours();
      return paymentHour < 8;
    }).length;
    
    const earlyMorningRevenue = transactions.filter(t => {
      const paymentHour = new Date(t.payment_date).getHours();
      return paymentHour < 8 && t.payment_method !== 'Complimentary';
    }).reduce((sum, t) => sum + (parseFloat(t.paid_amount) || 0), 0);
    
    const reportTypeLabel = reportType === 'shift' 
      ? 'SHIFT DAY REPORT (8am - 7:30am Next Day)' 
      : 'CALENDAR DAY REPORT (Midnight - Midnight)';
    
    // Create HTML for thermal printing with MAXIMUM CONTRAST
    // Using ONLY black (#000000) and white (#FFFFFF) - NO GRAYS
    const thermalHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Cashier Daily Report - ${hotelName}</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 0mm;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Courier New', 'Courier', 'Lucida Console', monospace;
            font-size: 11px;
            line-height: 1.4;
            background: white;
            padding: 2mm;
            width: 80mm;
            margin: 0 auto;
          }
          
          .thermal-container {
            width: 100%;
            max-width: 76mm;
            margin: 0 auto;
          }
          
          /* ALL TEXT MUST BE BOLD AND BLACK - NO EXCEPTIONS */
          .header-main {
            text-align: center;
            font-weight: 900;
            font-size: 20px;
            letter-spacing: 2px;
            margin-bottom: 5px;
            color: #000000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .header-sub {
            text-align: center;
            font-weight: 900;
            font-size: 14px;
            margin-bottom: 5px;
            color: #000000;
          }
          
          .header-details {
            text-align: center;
            font-weight: 700;
            font-size: 10px;
            margin-bottom: 3px;
            color: #000000;
          }
          
          .section-title {
            font-weight: 900;
            font-size: 12px;
            margin-top: 8px;
            margin-bottom: 6px;
            padding-bottom: 3px;
            border-bottom: 3px solid #000000;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #000000;
          }
          
          .divider {
            border-top: 2px solid #000000;
            margin: 6px 0;
          }
          
          .divider-dashed {
            border-top: 1px dashed #000000;
            margin: 6px 0;
          }
          
          .row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
            font-size: 10px;
          }
          
          .row-label {
            font-weight: 900;
            color: #000000;
          }
          
          .row-value {
            font-weight: 900;
            color: #000000;
          }
          
          /* Stats grid with SOLID BLACK borders */
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
            margin: 8px 0;
          }
          
          .stat-card {
            border: 2px solid #000000;
            padding: 8px 4px;
            text-align: center;
            background: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .stat-label {
            font-size: 9px;
            font-weight: 900;
            color: #000000;
            margin-bottom: 3px;
            letter-spacing: 0.5px;
          }
          
          .stat-value {
            font-size: 14px;
            font-weight: 900;
            color: #000000;
          }
          
          /* Payment grid with SOLID BLACK borders */
          .payment-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
            margin: 8px 0;
          }
          
          .payment-card {
            border: 2px solid #000000;
            padding: 6px;
            text-align: center;
            background: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .payment-label {
            font-size: 9px;
            font-weight: 900;
            color: #000000;
            margin-bottom: 3px;
          }
          
          .payment-amount {
            font-size: 12px;
            font-weight: 900;
            color: #000000;
          }
          
          .payment-count {
            font-size: 8px;
            font-weight: 700;
            color: #000000;
          }
          
          /* Transaction items - MAXIMUM CONTRAST */
          .transaction-item {
            margin-bottom: 12px;
            padding: 8px;
            border: 2px solid #000000;
            background: #ffffff;
            page-break-inside: avoid;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .transaction-early {
            border: 3px solid #000000;
            background: #ffffff;
            position: relative;
          }
          
          .transaction-early::before {
            content: "🌙 EARLY";
            position: absolute;
            top: -8px;
            right: 8px;
            background: #000000;
            color: #ffffff;
            font-size: 7px;
            font-weight: 900;
            padding: 1px 4px;
            letter-spacing: 1px;
          }
          
          .transaction-header {
            font-weight: 900;
            font-size: 11px;
            margin-bottom: 6px;
            padding-bottom: 4px;
            border-bottom: 2px solid #000000;
            display: flex;
            justify-content: space-between;
          }
          
          .transaction-detail {
            font-size: 9px;
            margin-bottom: 4px;
            display: flex;
            justify-content: space-between;
            font-weight: 700;
          }
          
          .transaction-detail span {
            font-weight: 700;
            color: #000000;
          }
          
          .order-number {
            font-weight: 900;
            font-size: 11px;
          }
          
          .amount-paid {
            font-weight: 900;
            font-size: 12px;
            color: #000000;
          }
          
          .amount-complimentary {
            font-weight: 900;
            font-size: 12px;
            color: #000000;
            background: #000000;
            color: #ffffff;
            padding: 2px 6px;
          }
          
          .items-section {
            margin-top: 6px;
            padding-top: 4px;
            border-top: 2px solid #000000;
          }
          
          .item-row {
            display: flex;
            justify-content: space-between;
            font-size: 9px;
            margin-bottom: 3px;
            padding-left: 4px;
            font-weight: 700;
          }
          
          .item-name {
            font-weight: 900;
          }
          
          .early-badge {
            display: inline-block;
            background: #000000;
            color: #ffffff;
            font-weight: 900;
            padding: 2px 6px;
            font-size: 8px;
            margin-left: 8px;
            letter-spacing: 1px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .footer {
            text-align: center;
            margin-top: 12px;
            padding-top: 8px;
            border-top: 3px solid #000000;
            font-size: 8px;
            font-weight: 700;
            color: #000000;
          }
          
          .warning-note {
            background: #000000;
            color: #ffffff;
            padding: 6px;
            text-align: center;
            font-weight: 900;
            font-size: 9px;
            margin: 8px 0;
            letter-spacing: 1px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .warning-note span {
            color: #ffffff;
            font-weight: 900;
          }
          
          /* ALL TEXT MUST BE BLACK - NO GRAY */
          p, div, span, .text, .detail, .info {
            color: #000000 !important;
          }
          
          @media print {
            body {
              margin: 0;
              padding: 2mm;
            }
            .no-break {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="thermal-container">
          <!-- Header Section -->
          <div style="text-align: center; margin-bottom: 12px;">
            <div class="header-main">${hotelName}</div>
            ${hotelAddress ? `<div class="header-details">${hotelAddress}</div>` : ''}
            ${hotelPhone ? `<div class="header-details">TEL: ${hotelPhone}</div>` : ''}
            <div class="divider"></div>
            <div class="header-sub">CASHIER DAILY REPORT</div>
            <div class="divider-dashed"></div>
            <div class="row">
              <span class="row-label">CASHIER:</span>
              <span class="row-value">${getCurrentStaffName().toUpperCase()}</span>
            </div>
            <div class="row">
              <span class="row-label">ID:</span>
              <span class="row-value">${getCurrentStaffId() || 'N/A'}</span>
            </div>
            <div class="row">
              <span class="row-label">${reportType === 'shift' ? 'SHIFT DATE:' : 'CALENDAR DATE:'}</span>
              <span class="row-value">${reportDate}</span>
            </div>
            ${selectedShiftDetails ? `
            <div class="row">
              <span class="row-label">SHIFT:</span>
              <span class="row-value">#${selectedShiftDetails.id} (${selectedShiftDetails.shift_start_time} - ${selectedShiftDetails.shift_end_time})</span>
            </div>
            <div class="row">
              <span class="row-label">STATUS:</span>
              <span class="row-value">${selectedShiftDetails.is_cashier_closed ? 'CLOSED' : 'OPEN'}</span>
            </div>
            ` : selectedShift === 'all' ? `
            <div class="row">
              <span class="row-label">SHIFTS:</span>
              <span class="row-value">ALL ${shifts.length} SHIFTS</span>
            </div>
            ` : ''}
            <div class="row">
              <span class="row-label">PRINTED:</span>
              <span class="row-value">${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}</span>
            </div>
            <div class="divider"></div>
            <div class="header-details" style="font-weight:900;">${reportTypeLabel}</div>
            <div class="divider"></div>
          </div>
          
          ${reportType === 'shift' ? `
          <div class="warning-note">
            <span>🌙 EARLY MORNING: Transactions before 8:00 AM belong to previous shift</span>
          </div>
          ` : ''}
          
          <!-- Statistics Section -->
          <div class="section-title">PERFORMANCE SUMMARY</div>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-label">TOTAL REVENUE</div>
              <div class="stat-value">${formatCurrency(stats.total_revenue || 0)}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">TOTAL ORDERS</div>
              <div class="stat-value">${stats.total_orders || 0}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">AVG ORDER VALUE</div>
              <div class="stat-value">${formatCurrency(stats.average_order_value || 0)}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">ACTIVE WAITERS</div>
              <div class="stat-value">${waiterStats.filter(w => w.total_orders > 0).length}</div>
            </div>
          </div>
          
          <div class="divider"></div>
          
          <!-- Payment Breakdown -->
          <div class="section-title">PAYMENT BREAKDOWN</div>
          <div class="payment-grid">
            <div class="payment-card">
              <div class="payment-label">CASH</div>
              <div class="payment-amount">${formatCurrency(paymentStatsData.Cash?.amount || 0)}</div>
              <div class="payment-count">${paymentStatsData.Cash?.count || 0} TXNS</div>
            </div>
            <div class="payment-card">
              <div class="payment-label">POS TERMINAL</div>
              <div class="payment-amount">${formatCurrency(paymentStatsData['POS Terminal']?.amount || 0)}</div>
              <div class="payment-count">${paymentStatsData['POS Terminal']?.count || 0} TXNS</div>
            </div>
            <div class="payment-card">
              <div class="payment-label">BANK TRANSFER</div>
              <div class="payment-amount">${formatCurrency(paymentStatsData['Bank Transfer']?.amount || 0)}</div>
              <div class="payment-count">${paymentStatsData['Bank Transfer']?.count || 0} TXNS</div>
            </div>
            <div class="payment-card">
              <div class="payment-label">COMPLIMENTARY</div>
              <div class="payment-amount">${formatCurrency(paymentStatsData.Complimentary?.amount || 0)}</div>
              <div class="payment-count">${paymentStatsData.Complimentary?.count || 0} TXNS</div>
            </div>
          </div>
          
          <div class="divider"></div>
          
          <!-- Order Type Breakdown -->
          ${Object.keys(orderTypeStats).length > 0 ? `
          <div class="section-title">ORDER TYPES</div>
          ${Object.entries(orderTypeStats).map(([type, data]) => `
            <div class="row">
              <span class="row-label">${ORDER_TYPES[type]?.label?.toUpperCase() || type.toUpperCase()}:</span>
              <span class="row-value">${data.count} ORDERS (${formatCurrency(data.revenue)})</span>
            </div>
          `).join('')}
          <div class="divider"></div>
          ` : ''}
          
          <!-- Early Morning Stats -->
          <div class="section-title">EARLY MORNING STATS (BEFORE 8AM)</div>
          <div class="row">
            <span class="row-label">TRANSACTIONS:</span>
            <span class="row-value">${earlyMorningCount}</span>
          </div>
          <div class="row">
            <span class="row-label">REVENUE:</span>
            <span class="row-value">${formatCurrency(earlyMorningRevenue)}</span>
          </div>
          
          <div class="divider"></div>
          
          <!-- TRANSACTIONS SECTION -->
          <div class="section-title">TRANSACTIONS (${transactions.length})</div>
          
          ${transactions.map((transaction, index) => {
            const paymentHour = new Date(transaction.payment_date).getHours();
            const isEarlyMorning = paymentHour < 8;
            const orderType = ORDER_TYPES[transaction.order_type]?.label?.toUpperCase() || transaction.order_type?.toUpperCase() || 'N/A';
            const tableInfo = transaction.table_number ? `TABLE ${transaction.table_number}` : 
                              transaction.room_number ? `ROOM ${transaction.room_number}` : 'WALK-IN';
            const guestName = transaction.guest_name === 'Walk-in Customer' ? 'WALK-IN' : (transaction.guest_name?.toUpperCase() || 'GUEST');
            const waiterName = transaction.waiter_name?.toUpperCase() || 'N/A';
            const paymentMethod = transaction.payment_method?.toUpperCase() || 'N/A';
            const isComplimentary = transaction.payment_method === 'Complimentary';
            
            return `
              <div class="transaction-item ${isEarlyMorning ? 'transaction-early' : ''} no-break">
                <div class="transaction-header">
                  <span class="order-number">#${transaction.order_number || 'N/A'}</span>
                  ${isComplimentary ? 
                    `<span class="amount-complimentary">COMPLIMENTARY</span>` : 
                    `<span class="amount-paid">${formatCurrency(transaction.paid_amount)}</span>`
                  }
                </div>
                <div class="transaction-detail">
                  <span>${orderType} | ${tableInfo}</span>
                  <span>${paymentMethod}</span>
                </div>
                <div class="transaction-detail">
                  <span>WAITER: ${waiterName}</span>
                  <span>TIME: ${formatTime(transaction.payment_date)}${isEarlyMorning ? ' 🌙' : ''}</span>
                </div>
                <div class="transaction-detail">
                  <span>GUEST: ${guestName}</span>
                  <span>ITEMS: ${transaction.items?.length || 1}</span>
                </div>
                ${transaction.items && transaction.items.length > 0 ? `
                  <div class="items-section">
                    ${transaction.items.map(item => {
                      const quantity = item.quantity || item.qty || 1;
                      const itemName = (item.item_name || item.name || item.product_name || 'ITEM').toUpperCase();
                      const itemPrice = item.price || item.unit_price || 0;
                      const itemTotal = quantity * itemPrice;
                      return `
                        <div class="item-row">
                          <span class="item-name">${quantity}X ${itemName}</span>
                          <span>${formatCurrency(itemTotal)}</span>
                        </div>
                      `;
                    }).join('')}
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
          
          ${transactions.length === 0 ? `
          <div style="text-align: center; padding: 20px 0; border: 2px solid #000; margin: 10px 0;">
            <div class="row-label" style="font-size: 12px;">NO TRANSACTIONS FOUND</div>
          </div>
          ` : ''}
          
          <!-- Footer -->
          <div class="footer">
            <div>${hotelName.toUpperCase()} F&B SYSTEM</div>
            <div>REPORT BY: ${getCurrentStaffName().toUpperCase()}</div>
            <div>THANK YOU FOR YOUR SERVICE</div>
            <div style="margin-top: 6px;">${'='.repeat(32)}</div>
          </div>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 2000);
            }, 500);
          }
        </script>
      </body>
      </html>
    `;
    
    // Create print window with enhanced thermal styles
    const printWindow = window.open('', '_blank', 'width=450,height=650,toolbar=yes,scrollbars=yes,menubar=yes');
    if (printWindow) {
      printWindow.document.write(thermalHTML);
      printWindow.document.close();
      showSnackbar('Thermal report printed with maximum contrast', 'success');
    } else {
      showSnackbar('Please allow pop-ups to print the report', 'error');
    }
  } catch (error) {
    console.error('Error printing thermal report:', error);
    showSnackbar('Failed to print report: ' + error.message, 'error');
  }
};
  // PDF Export
  const exportToPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const hotelName = hotel?.hotel_name || 'Frankville Hotels';
    const reportDate = formatDate(selectedDate);
    const stats = overallStats || {};
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;
    
    // Header
    doc.setFillColor(26, 35, 126);
    doc.rect(0, 0, pageWidth, 60, 'F');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(hotelName, pageWidth / 2, 18, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Cashier Daily Sales Report', pageWidth / 2, 30, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, pageWidth / 2, 40, { align: 'center' });
    
    // Report Type Info
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    const reportTypeLabel = reportType === 'shift' 
      ? 'Report Type: Shift Day (8:00 AM to 7:30 AM Next Day)' 
      : 'Report Type: Calendar Day (Midnight to Midnight)';
    doc.text(reportTypeLabel, pageWidth / 2, 48, { align: 'center' });
    
    // Shift info
    if (selectedShiftDetails) {
      doc.setFontSize(8);
      doc.setTextColor(200, 200, 200);
      doc.text(`Shift ID: ${selectedShiftDetails.id} | Cashier: ${selectedShiftDetails.staff_name} | ${selectedShiftDetails.shift_start_time} - ${selectedShiftDetails.shift_end_time}`, pageWidth / 2, 55, { align: 'center' });
    } else if (selectedShift && selectedShift !== 'all') {
      doc.setFontSize(8);
      doc.text(`Shift ID: ${selectedShift}`, pageWidth / 2, 55, { align: 'center' });
    } else if (shifts.length > 0) {
      doc.setFontSize(8);
      doc.text(`${shifts.length} Shift(s) Combined`, pageWidth / 2, 55, { align: 'center' });
    }
    
    yPos = 70;
    
    // Report Info
    doc.setFillColor(240, 242, 245);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORT INFORMATION', margin + 5, yPos + 8);
    doc.setFont('helvetica', 'normal');
    const dateRangeText = reportType === 'shift' 
      ? `Shift Date: ${reportDate} (8am - 7:30am next day)`
      : `Calendar Date: ${reportDate} (Midnight - Midnight)`;
    doc.text(dateRangeText, margin + 10, yPos + 16);
    doc.text(`Total Transactions: ${transactions.length}`, margin + 10, yPos + 22);
    yPos += 35;
    
    // Summary Cards
    const summaryCards = [
      { label: '💰 Total Revenue', value: formatCurrency(stats.total_revenue || 0), color: '#2E7D32' },
      { label: '📋 Total Orders', value: (stats.total_orders || 0).toString(), color: '#1976D2' },
      { label: '📊 Avg Order', value: formatCurrency(stats.average_order_value || 0), color: '#FF9800' },
      { label: '🎁 Complimentary', value: (paymentStats?.Complimentary?.count || 0).toString(), color: '#9E9E9E' }
    ];
    
    const cardWidth = (pageWidth - (margin * 2) - 12) / 4;
    summaryCards.forEach((card, idx) => {
      const x = margin + (idx * (cardWidth + 4));
      doc.setFillColor(250, 250, 250);
      doc.rect(x, yPos, cardWidth, 25, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(x, yPos, cardWidth, 25, 'D');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(card.label, x + 3, yPos + 7);
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(card.value, x + 3, yPos + 20);
    });
    yPos += 35;
    
    // Payment Breakdown
    doc.setFillColor(66, 66, 66);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('PAYMENT BREAKDOWN', margin + 5, yPos + 6);
    yPos += 15;
    
    const paymentMethods = [
      { label: 'Cash', amount: paymentStats?.Cash?.amount || 0, count: paymentStats?.Cash?.count || 0 },
      { label: 'POS Terminal', amount: paymentStats?.['POS Terminal']?.amount || 0, count: paymentStats?.['POS Terminal']?.count || 0 },
      { label: 'Bank Transfer', amount: paymentStats?.['Bank Transfer']?.amount || 0, count: paymentStats?.['Bank Transfer']?.count || 0 },
      { label: 'Complimentary', amount: paymentStats?.Complimentary?.amount || 0, count: paymentStats?.Complimentary?.count || 0 }
    ];
    
    paymentMethods.forEach((method, idx) => {
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, yPos + (idx * 10), pageWidth - (margin * 2), 9, 'F');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      const displayValue = `${formatCurrency(method.amount)} (${method.count} txns)`;
      doc.text(`${method.label}: ${displayValue}`, margin + 5, yPos + (idx * 10) + 6);
    });
    yPos += paymentMethods.length * 10 + 15;
    
    // Early Morning Note
    if (reportType === 'shift') {
      doc.setFontSize(8);
      doc.setTextColor(255, 152, 0);
      doc.text('🌙 Note: Transactions before 8:00 AM belong to the previous day\'s shift', margin + 5, yPos);
      yPos += 10;
    }
    
    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `${hotelName} F&B System - Page ${i} of ${totalPages} - Generated by ${getCurrentStaffName()}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    const fileName = `cashier_${reportType}_report_${format(new Date(selectedDate), 'yyyyMMdd')}${selectedShift && selectedShift !== 'all' ? `_shift_${selectedShift}` : ''}.pdf`;
    doc.save(fileName);
    showSnackbar('PDF report downloaded successfully', 'success');
  };
  
  // Excel Export
  const exportToExcel = () => {
    const rows = transactions.map(transaction => {
      const orderType = ORDER_TYPES[transaction.order_type]?.label || transaction.order_type || 'N/A';
      const tableInfo = transaction.table_number ? `Table ${transaction.table_number}` : 
                        transaction.room_number ? `Room ${transaction.room_number}` : 'Walk-in';
      const paymentTime = new Date(transaction.payment_date);
      const isEarlyMorning = paymentTime.getHours() < 8;
      
      return {
        'Order Number': transaction.order_number || 'N/A',
        'Payment Number': transaction.payment_number || 'N/A',
        'Waiter': transaction.waiter_name || 'N/A',
        'Order Type': orderType,
        'Table/Room': tableInfo,
        'Guest Name': transaction.guest_name || 'Guest',
        'Phone': transaction.guest_phone || 'N/A',
        'Items Count': transaction.items?.length || 0,
        'Payment Method': transaction.payment_method || 'N/A',
        'Amount (₦)': parseFloat(transaction.paid_amount || 0).toFixed(2),
        'Payment Time': formatTime(transaction.payment_date),
        'Payment Date': formatDate(transaction.payment_date),
        'Shift ID': transaction.payment_shift_id || transaction.order_shift_id || 'N/A',
        'Early Morning (Pre-8am)': isEarlyMorning ? 'Yes' : 'No',
        'Payment Status': transaction.payment_status || 'completed'
      };
    });
    
    rows.push({
      'Order Number': '=== SUMMARY ===',
      'Payment Number': '',
      'Waiter': '',
      'Order Type': '',
      'Table/Room': '',
      'Guest Name': '',
      'Phone': '',
      'Items Count': transactions.reduce((sum, t) => sum + (t.items?.length || 0), 0),
      'Payment Method': '',
      'Amount (₦)': (overallStats?.total_revenue || 0).toFixed(2),
      'Payment Time': '',
      'Payment Date': '',
      'Shift ID': selectedShift || 'ALL',
      'Early Morning (Pre-8am)': '',
      'Payment Status': ''
    });
    
    const headers = Object.keys(rows[0]);
    const csvRows = [
      headers.join(','),
      ...rows.map(row => headers.map(header => {
        const value = row[header]?.toString() || '';
        return `"${value.replace(/"/g, '""')}"`;
      }).join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cashier_${reportType}_report_${format(new Date(selectedDate), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showSnackbar('Excel report exported successfully', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  const handleLogout = () => {
    navigate('/HotelAdmin');
  };

  const handleNotificationClick = () => {};

  // Date navigation
  const goToPreviousDay = () => {
    const prevDate = subDays(new Date(selectedDate), 1);
    setSelectedDate(format(prevDate, 'yyyy-MM-dd'));
  };

  const goToNextDay = () => {
    const nextDate = subDays(new Date(selectedDate), -1);
    if (nextDate <= new Date()) {
      setSelectedDate(format(nextDate, 'yyyy-MM-dd'));
    }
  };

  const goToToday = () => {
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const getFilteredTransactions = () => {
    let filtered = [...transactions];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.order_number?.toLowerCase().includes(query) ||
        t.payment_number?.toLowerCase().includes(query) ||
        t.guest_name?.toLowerCase().includes(query) ||
        t.waiter_name?.toLowerCase().includes(query)
      );
    }
    
    if (filterWaiter !== 'all') {
      filtered = filtered.filter(t => t.waiter_staff_id === filterWaiter);
    }
    
    if (filterPaymentMethod !== 'all') {
      filtered = filtered.filter(t => t.payment_method === filterPaymentMethod);
    }
    
    return filtered;
  };

  const getPaymentMethodChip = (method) => {
    const methodInfo = PAYMENT_METHODS[method];
    return methodInfo ? (
      <Chip 
        icon={methodInfo.icon} 
        label={methodInfo.label} 
        size="small" 
        sx={{ bgcolor: `${methodInfo.color}20`, color: methodInfo.color }} 
      />
    ) : (method || 'N/A');
  };

  const getOrderTypeChip = (type) => {
    const typeInfo = ORDER_TYPES[type];
    return typeInfo ? (
      <Chip 
        icon={typeInfo.icon} 
        label={typeInfo.label} 
        size="small" 
        sx={{ bgcolor: `${typeInfo.color}20`, color: typeInfo.color }} 
      />
    ) : (type || 'N/A');
  };

  // Overview Metrics Component
  const OverviewMetrics = () => {
    const stats = overallStats || {};
    const paidCount = transactions.filter(t => t.payment_method !== 'Complimentary').length;
    const compCount = transactions.filter(t => t.payment_method === 'Complimentary').length;
    
    const metrics = [
      { label: 'Total Revenue', value: formatCurrency(stats.total_revenue || 0), icon: <AttachMoney />, color: '#2E7D32' },
      { label: 'Total Orders', value: stats.total_orders || 0, icon: <Receipt />, color: '#1976D2' },
      { label: 'Avg Order Value', value: formatCurrency(stats.average_order_value || 0), icon: <TrendingUp />, color: '#FF9800' },
      { label: 'Paid Transactions', value: paidCount, icon: <CheckCircle />, color: '#4CAF50' },
      { label: 'Complimentary', value: compCount, icon: <School />, color: '#9E9E9E' },
      { label: 'Active Staff', value: waiterStats.filter(w => w.total_orders > 0).length, icon: <People />, color: '#9C27B0' }
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metrics.map((metric, idx) => (
          <Grid item xs={6} sm={4} md={2} key={idx}>
            <StatsCard>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Avatar sx={{ bgcolor: metric.color, width: 40, height: 40 }}>{metric.icon}</Avatar>
                </Box>
                <Typography variant="h6" fontWeight="bold">{metric.value}</Typography>
                <Typography variant="caption" color="textSecondary">{metric.label}</Typography>
              </CardContent>
            </StatsCard>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Payment Breakdown Component
  const PaymentBreakdownComponent = () => {
    const stats = paymentStats || {};
    
    const methods = [
      { method: 'Cash', label: 'Cash', icon: <AttachMoney />, color: '#4CAF50', amount: stats.Cash?.amount || 0, count: stats.Cash?.count || 0 },
      { method: 'POS', label: 'POS Terminal', icon: <CreditCard />, color: '#2196F3', amount: stats['POS Terminal']?.amount || 0, count: stats['POS Terminal']?.count || 0 },
      { method: 'Transfer', label: 'Bank Transfer', icon: <Payments />, color: '#9C27B0', amount: stats['Bank Transfer']?.amount || 0, count: stats['Bank Transfer']?.count || 0 },
      { method: 'Complimentary', label: 'Complimentary', icon: <School />, color: '#9E9E9E', amount: stats.Complimentary?.amount || 0, count: stats.Complimentary?.count || 0 }
    ];
    
    const totalRevenue = overallStats?.total_revenue || 0;
    
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          💳 Payment Method Breakdown
        </Typography>
        <Grid container spacing={2}>
          {methods.map(method => (
            <Grid item xs={12} sm={6} md={3} key={method.method}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, border: `1px solid ${method.color}30`, bgcolor: `${method.color}08` }}>
                <Avatar sx={{ bgcolor: method.color, width: 40, height: 40 }}>{method.icon}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="textSecondary">{method.label}</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(method.amount)}</Typography>
                  <Typography variant="caption" sx={{ color: method.color }}>{method.count} transaction(s)</Typography>
                  {totalRevenue > 0 && method.amount > 0 && method.method !== 'Complimentary' && (
                    <Typography variant="caption" sx={{ color: method.color, display: 'block' }}>
                      {((method.amount / totalRevenue) * 100).toFixed(1)}% of revenue
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">Report Type</Typography>
            <Chip 
              icon={reportType === 'shift' ? <AccessTime /> : <Today />} 
              label={reportType === 'shift' ? 'Shift Day (8am - 7:30am next day)' : 'Calendar Day (Midnight - Midnight)'} 
              size="small" 
              sx={{ mt: 1, bgcolor: reportType === 'shift' ? '#FF9800' : '#1976D2', color: 'white' }} 
            />
            <Typography variant="caption" display="block" sx={{ mt: 1, color: '#FF9800' }}>
              {reportType === 'shift' && '🌙 Transactions before 8am belong to previous day\'s shift'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">Shift Information</Typography>
            {selectedShiftDetails ? (
              <>
                <Chip 
                  icon={<AccessTime />} 
                  label={`Shift #${selectedShiftDetails.id}`} 
                  size="small" 
                  sx={{ mt: 1, bgcolor: '#1976D2', color: 'white' }} 
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Cashier: {selectedShiftDetails.staff_name}<br />
                  Time: {selectedShiftDetails.shift_start_time} - {selectedShiftDetails.shift_end_time}<br />
                  Status: {selectedShiftDetails.is_cashier_closed ? 'Closed' : 'Open'}
                </Typography>
              </>
            ) : selectedShift === 'all' ? (
              <Chip icon={<History />} label={`All ${shifts.length} Shifts Combined`} size="small" sx={{ mt: 1 }} />
            ) : shifts.length > 0 ? (
              <Chip icon={<History />} label={`${shifts.length} Shift(s) Available`} size="small" sx={{ mt: 1 }} />
            ) : (
              <Typography variant="body2" color="textSecondary">No shift data available</Typography>
            )}
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">Day Status</Typography>
            {dayClosure ? (
              <>
                <Chip icon={<Lock />} label="Day Closed" color="error" size="small" sx={{ mt: 1 }} />
                <Typography variant="body2">Closed by: {dayClosure.closed_by_cashier_name}</Typography>
                <Typography variant="body2">Closed at: {formatDateTime(dayClosure.closed_at)}</Typography>
              </>
            ) : (
              <Chip icon={<LockOpen />} label="Day Open" color="success" size="small" sx={{ mt: 1 }} />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">Early Morning Stats</Typography>
            {(() => {
              const earlyMorningCount = transactions.filter(t => {
                const paymentHour = new Date(t.payment_date).getHours();
                return paymentHour < 8;
              }).length;
              const earlyMorningRevenue = transactions.filter(t => {
                const paymentHour = new Date(t.payment_date).getHours();
                return paymentHour < 8 && t.payment_method !== 'Complimentary';
              }).reduce((sum, t) => sum + (parseFloat(t.paid_amount) || 0), 0);
              return (
                <>
                  <Typography variant="body2">{earlyMorningCount} transactions before 8am</Typography>
                  <Typography variant="body2" fontWeight="bold">{formatCurrency(earlyMorningRevenue)} revenue before 8am</Typography>
                </>
              );
            })()}
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Order Type Breakdown
  const OrderTypeBreakdownComponent = () => {
    if (Object.keys(orderTypeStats).length === 0) return null;
    
    const totalRevenue = overallStats?.total_revenue || 0;
    
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          📊 Order Type Breakdown
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(orderTypeStats).map(([type, data]) => {
            const typeInfo = ORDER_TYPES[type] || { label: type, icon: <Restaurant />, color: '#757575' };
            const percentage = totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0;
            return (
              <Grid item xs={12} sm={6} md={4} key={type}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, border: `1px solid ${typeInfo.color}30`, bgcolor: `${typeInfo.color}08` }}>
                  <Avatar sx={{ bgcolor: typeInfo.color, width: 40, height: 40 }}>{typeInfo.icon}</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="textSecondary">{typeInfo.label}</Typography>
                    <Typography variant="h6" fontWeight="bold">{data.count} orders</Typography>
                    <Typography variant="caption" sx={{ color: typeInfo.color }}>{formatCurrency(data.revenue)}</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={percentage} 
                      sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: `${typeInfo.color}20`, '& .MuiLinearProgress-bar': { bgcolor: typeInfo.color } }}
                    />
                    <Typography variant="caption" sx={{ color: typeInfo.color }}>{percentage.toFixed(1)}% of revenue</Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  };

  // Hourly Breakdown Component
  const HourlyBreakdownComponent = () => {
    if (hourlyBreakdown.length === 0) return null;
    
    const maxRevenue = Math.max(...hourlyBreakdown.map(h => h.revenue), 1);
    
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          ⏰ Hourly Transaction Breakdown
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 200, mt: 3, overflowX: 'auto', pb: 2 }}>
          {hourlyBreakdown.map((hour, idx) => (
            <Box key={idx} sx={{ flex: 1, minWidth: 60, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Tooltip title={`₦${hour.revenue.toLocaleString()} from ${hour.transaction_count} transactions`}>
                <Box sx={{ 
                  width: '100%', 
                  maxWidth: 70, 
                  backgroundColor: hour.hour < 8 ? '#FF9800' : '#4CAF50',
                  height: `${(hour.revenue / maxRevenue) * 150}px`, 
                  minHeight: hour.revenue > 0 ? '8px' : '0', 
                  borderRadius: '8px 8px 0 0', 
                  transition: 'all 0.3s ease', 
                  cursor: 'pointer', 
                  '&:hover': { opacity: 0.8, transform: 'scaleX(1.02)' } 
                }} />
              </Tooltip>
              <Typography variant="caption" sx={{ mt: 1, fontWeight: 'medium' }}>{hour.hour_display}</Typography>
              <Typography variant="caption" fontWeight="bold" color={hour.hour < 8 ? '#FF9800' : '#4CAF50'}>
                ₦{hour.revenue.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="textSecondary">({hour.transaction_count})</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#FF9800' }}>
          🌙 Hours before 8:00 AM belong to previous day's shift
        </Typography>
      </Paper>
    );
  };

  // Transactions Table
  const TransactionsTable = () => {
    const filteredTransactions = getFilteredTransactions();
    
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (filteredTransactions.length === 0) {
      return (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Receipt sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">No transactions found</Typography>
          <Typography variant="body2" color="textSecondary">
            {selectedShift && selectedShift !== 'all' 
              ? `No transactions found for Shift #${selectedShift} on ${formatDate(selectedDate)}`
              : `No transactions found for ${reportType === 'shift' ? 'shift day' : 'calendar day'} ${formatDate(selectedDate)}`}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
            {reportType === 'shift' && '🌙 Remember: Shift day runs from 8:00 AM to 7:30 AM the next day'}
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">📋 All Transactions ({filteredTransactions.length})</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search by order #, guest..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}><Clear fontSize="small" /></IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ width: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={filterWaiter}
                  onChange={(e) => setFilterWaiter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">All Waiters</MenuItem>
                  {waiterStats.map(waiter => (
                    <MenuItem key={waiter.waiter_id} value={waiter.waiter_id}>{waiter.waiter_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={filterPaymentMethod}
                  onChange={(e) => setFilterPaymentMethod(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  {Object.keys(PAYMENT_METHODS).map(method => (
                    <MenuItem key={method} value={method}>{PAYMENT_METHODS[method].label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
        
        <TableContainer sx={{ maxHeight: 'calc(100vh - 600px)', overflowX: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Shift ID</TableCell>
                <TableCell>Waiter</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Table/Room</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Items</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Early AM</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.map((transaction) => {
                const paymentHour = new Date(transaction.payment_date).getHours();
                const isEarlyMorning = paymentHour < 8;
                
                return (
                  <TableRow 
                    key={transaction.payment_id} 
                    hover 
                    sx={{ 
                      backgroundColor: isEarlyMorning ? '#FFF8E1' : 'inherit',
                      '&:hover': { backgroundColor: isEarlyMorning ? '#FFECB3' : '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{transaction.order_number || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`#${transaction.payment_shift_id || transaction.order_shift_id || 'N/A'}`} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontSize: '10px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.waiter_name || 'Unknown'} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontSize: '10px' }}
                      />
                    </TableCell>
                    <TableCell>{getOrderTypeChip(transaction.order_type)}</TableCell>
                    <TableCell>
                      {transaction.table_number ? `Table ${transaction.table_number}` : 
                       transaction.room_number ? `Room ${transaction.room_number}` : 'Walk-in'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={transaction.guest_phone || ''}>
                        <span>{transaction.guest_name || 'Guest'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">{transaction.items?.length || 1}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" sx={{ color: transaction.payment_method === 'Complimentary' ? '#9E9E9E' : '#2E7D32' }}>
                        {formatCurrency(transaction.paid_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getPaymentMethodChip(transaction.payment_method)}</TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(transaction.payment_date)}>
                        <span style={{ color: isEarlyMorning ? '#FF9800' : 'inherit' }}>
                          {formatTime(transaction.payment_date)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {isEarlyMorning ? (
                        <Chip 
                          icon={<AccessTime />} 
                          label="Pre-8am" 
                          size="small" 
                          sx={{ bgcolor: '#FF9800', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          ml: isMobile ? 0 : sidebarOpen ? '240px' : '65px',
          transition: 'margin 0.2s ease-in-out, width 0.2s ease-in-out',
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%'
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />
        
        <Box sx={{ mt: 2, mb: 3 }}>
          <ReportHeader elevation={0}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    🧾 Cashier Daily Report
                  </Typography>
                  <Typography variant="subtitle1">
                    {getCurrentStaffName()} • {getCurrentStaffId()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<Today />} label={format(new Date(selectedDate), 'EEEE, dd MMMM yyyy')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                    <Chip icon={<Receipt />} label={`Total Transactions: ${transactions.length}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                    {dayClosure ? (
                      <Chip icon={<Lock />} label="Day Closed" sx={{ bgcolor: '#f44336', color: 'white' }} />
                    ) : (
                      <Chip icon={<LockOpen />} label="Day Open" sx={{ bgcolor: '#4CAF50', color: 'white' }} />
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                  {/* Report Type Toggle */}
                  <ToggleButtonGroup
                    value={reportType}
                    exclusive
                    onChange={handleReportTypeChange}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1 }}
                  >
                    <ToggleButton value="shift" sx={{ color: 'white', '&.Mui-selected': { bgcolor: '#FF9800', color: 'white' } }}>
                      <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                      Shift Day (8am-7:30am)
                    </ToggleButton>
                    <ToggleButton value="calendar" sx={{ color: 'white', '&.Mui-selected': { bgcolor: '#1976D2', color: 'white' } }}>
                      <Today fontSize="small" sx={{ mr: 0.5 }} />
                      Calendar Day (Midnight)
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Shift Filter Dropdown */}
                    <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1 }}>
                      <InputLabel sx={{ color: 'white' }}>Filter by Shift</InputLabel>
                      <Select
                        value={selectedShift}
                        onChange={(e) => setSelectedShift(e.target.value)}
                        label="Filter by Shift"
                        sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                      >
                        <MenuItem value="">All Shifts (Combined)</MenuItem>
                        {shifts.map((shift) => (
                          <MenuItem key={shift.id} value={shift.id}>
                            Shift #{shift.id} - {shift.staff_name} ({shift.shift_start_time} - {shift.shift_end_time})
                            {shift.is_cashier_closed ? ' ✓ Closed' : ' ○ Open'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <IconButton onClick={goToPreviousDay} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}>
                      <NavigateBefore />
                    </IconButton>
                    <TextField
                      type="date"
                      size="small"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: 'white' } }}
                    />
                    <IconButton onClick={goToNextDay} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}>
                      <NavigateNext />
                    </IconButton>
                    <Button variant="contained" size="small" onClick={goToToday} sx={{ bgcolor: 'white', color: '#1a237e' }}>
                      Today
                    </Button>
                    <Tooltip title="Refresh">
                      <IconButton onClick={refreshData} disabled={refreshing} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}>
                        {refreshing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ position: 'absolute', right: -30, bottom: -30, opacity: 0.08 }}>
              <Receipt sx={{ fontSize: 200 }} />
            </Box>
          </ReportHeader>
        </Box>
        
        {/* Info Alert for Shift Day Report */}
        {reportType === 'shift' && (
          <Alert severity="info" icon={<AccessTime />} sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Shift Day Report (8:00 AM - 7:30 AM Next Day)</strong><br />
              Transactions before 8:00 AM are displayed with 🟠 orange highlighting and belong to the previous day's shift.
              This ensures accurate shift-based reconciliation.
            </Typography>
          </Alert>
        )}
        
        {/* Export Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            startIcon={<Print />} 
            onClick={printThermalReport} 
            sx={{ 
              borderRadius: 2, 
              bgcolor: '#2E7D32', 
              '&:hover': { bgcolor: '#1B5E20' },
              fontWeight: 'bold'
            }}
          >
            Print Thermal Report
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={exportToPDF} sx={{ borderRadius: 2 }}>
            Export PDF
          </Button>
          <Button variant="outlined" startIcon={<Description />} onClick={exportToExcel} sx={{ borderRadius: 2 }}>
            Export Excel
          </Button>
        </Box>
        
        {/* Main Content */}
        <Box sx={{ mt: 2 }}>
          <OverviewMetrics />
          <PaymentBreakdownComponent />
          <OrderTypeBreakdownComponent />
          <HourlyBreakdownComponent />
          <TransactionsTable />
        </Box>
      </Box>
      
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashierDailyReport;